import '.a{display:flex;}.ssr-plugin'
import '.b{flex-direction:column;}.ssr-plugin'
import '.ab{align-items:stretch;}.ssr-plugin'
import '.er{gap:0.38rem;}.ssr-plugin'
import '.fk{color:var(--primary-red);}.ssr-plugin'
import '.c{align-items:center;}.ssr-plugin'
import '.an{gap:0.5rem;}.ssr-plugin'
import '.e5{border-radius:0.75rem;}.ssr-plugin'
import '.e1{font-size:1.125rem;}.ssr-plugin'
import '.fl{height:1.75rem;}.ssr-plugin'
import '.by{flex-grow:1;}.ssr-plugin'
import '.bd{padding:0px;}.ssr-plugin'
import '.bb{border:none;}.ssr-plugin'
import '.es{outline:none;}.ssr-plugin'
import '.et{background:transparent;}.ssr-plugin'
import { Style, styled } from 'inlines'
import { type IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import { faPaperPlaneTop } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  useCallback,
  KeyboardEvent,
  MutableRefObject,
  forwardRef,
  FocusEventHandler,
  KeyboardEventHandler,
  ReactNode,
} from 'react'
import { Text } from '../../typography/Text'
import { FileSelector, FileSelectorProps } from './FileSelector'

export type TextInputProps = {
  text: string
  placeholder?: string
  label?: string
  labelStyle?: Style
  displayLabelAsterisk?: boolean
  disabled?: boolean
  errorMessage?: string
  icon?: IconDefinition
  submitIcon?: IconDefinition
  fileSelector?: FileSelectorProps
  endAdornment?: ReactNode
  style?: Style
  containerStyle?: Style
  ref?: MutableRefObject<HTMLInputElement>
  setText: (text: string) => void
  onSubmit?: () => void
  onFocus?: FocusEventHandler<HTMLInputElement>
  onBlur?: FocusEventHandler<HTMLInputElement>
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      text,
      placeholder,
      label,
      labelStyle,
      displayLabelAsterisk,
      disabled,
      errorMessage,
      icon,
      submitIcon,
      fileSelector,
      endAdornment,
      style,
      containerStyle,
      setText,
      onSubmit,
      onFocus,
      onBlur,
      onKeyDown,
    }: TextInputProps,
    ref,
  ) => {
    const errorStateOn = !disabled && !!errorMessage
    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      if (onKeyDown) {
        onKeyDown(event)
      }
      if (onSubmit && event.key === 'Enter') {
        onSubmit()
      }
    }
    const border = useCallback(
      (color: string) =>
        `1px solid var(${errorStateOn ? '--primary-red' : color})`,
      [errorStateOn],
    )

    return (
      <styled.div className="a b ab er"
        style={{
          '&:has(:disabled)': {
            cursor: 'not-allowed',
          },
          ...style,
        }}
      >
        {label && (
          <Text.Medium
            style={{ color: 'var(--shades-blue-500)', ...labelStyle }}
          >
            {label}
            {displayLabelAsterisk && ' '}
            {displayLabelAsterisk && (
              <span className="fk">
                *
              </span>
            )}
          </Text.Medium>
        )}
        <styled.div className="a c an e5"
          style={{
            padding: icon ? '0.5rem 0.69rem' : '0.5rem 1rem',
            border: border('--shades-blue-300'),
            '&:hover': {
              border: border('--shades-blue-500'),
            },
            '&:focus-within': {
              border: border('--primary-blue'),
            },
            '&:has(:disabled)': {
              border: 'var(--gray-border)',
              background: 'var(--gray-100)',
            },
            background: errorStateOn
              ? 'var(--shades-red-100)'
              : 'var(--shades-white)',
            ...containerStyle,
          }}
        >
          {icon && (
            <FontAwesomeIcon
              icon={icon}
              style={{
                color: errorStateOn
                  ? 'var(--shades-red-400)'
                  : 'var(--shades-blue-200)',
                height: '1.5rem',
                width: '1.5rem',
                marginRight: '0.24rem',
              }}
            />
          )}
          <styled.input className="e1 fl by bd bb es et"
            placeholder={placeholder}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            ref={ref}
            style={{
              '&::placeholder': {
                color: errorStateOn
                  ? 'var(--shades-red-400)'
                  : 'var(--gray-300)',
                userSelect: 'none',
              },
              '&:disabled': {
                cursor: 'not-allowed',
              },
            }}
          ></styled.input>
          {endAdornment}
          {fileSelector && (
            <FileSelector
              {...fileSelector}
              iconStyle={{
                color: 'var(--shades-blue-200)',
                fontSize: '1.125rem',
                width: '2rem',
                cursor: 'pointer',
                ...fileSelector.iconStyle,
              }}
            />
          )}
          {onSubmit && (
            <>
              <FontAwesomeIcon
                icon={submitIcon ?? faPaperPlaneTop}
                onClick={onSubmit}
                style={{
                  height: '1.125rem',
                  width: '2rem',
                  color: 'var(--shades-blue-200)',
                  cursor: 'pointer',
                }}
              />
            </>
          )}
        </styled.div>
        {errorStateOn && (
          <Text.ExtraSmall style={{ color: 'var(--shades-red-600)' }}>
            {errorMessage}
          </Text.ExtraSmall>
        )}
      </styled.div>
    )
  },
)

TextInput.displayName = 'TextInput'
